import { Button, Flex, FloraButton, Input, Text, Tooltip } from '@grupoboticario/flora-react';
import { EyeIcon, EyeOffIcon, QuestionMarkCircleIcon } from '@grupoboticario/flora-react-icons';
import config from 'config';
import { user } from 'core';
import type { AuthCredentials } from 'core/domains/auth';
import { DEFAULT_ACCESS_TOKEN_EXPIRES_IN, setAccessToken } from 'core/services/auth';
import { slugify } from 'core/utilities/strings/slugify';
import { useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { ResponsiveFloraTypography } from 'ui/components';
import { useRedirectToHome } from 'ui/hooks/useRedirectToHome';
import { getUsernameMethod, parseJwt } from '../helpers';
import { useLoginForm, useLoginRequest } from '../hooks';
import Styles from './Login.components.styles';
import { MessageError } from './MessageError';

export const Form = () => {
  const dispatch = useDispatch();
  const { redirectToHome } = useRedirectToHome();
  const {
    handleTracking,
    handleOnTooltipTracking,
    handleSubmit,
    passwordType,
    showPassword,
    register,
    getValues,
    validationErrors,
    isValid,
    isSubmitting,
  } = useLoginForm();

  const { data, getLogin, isSuccess, isError, isLoading, error } = useLoginRequest();

  const onSubmit: SubmitHandler<AuthCredentials> = async (inputData) => {
    const authCredentials = {
      username: inputData.username.trim(),
      password: inputData.password.trim(),
    };

    await getLogin(authCredentials);
  };

  useEffect(() => {
    const usernameMethod = getUsernameMethod(getValues('username'));

    if (isSuccess) {
      const resellerId = data?.id_token || parseJwt(data?.access_token || '')?.altid;

      window.dataLayer.push({
        event: 'analytics-event',
        event_parameters: {
          event_name: 'callback_login_sucesso',
          userid: resellerId || '',
          cd_method: usernameMethod,
          cd_codigo_representante: resellerId || '',
        },
      });
      if (data?.access_token) {
        setAccessToken({
          token: data.access_token,
          expiresIn: data?.expires_in || DEFAULT_ACCESS_TOKEN_EXPIRES_IN,
        });
        dispatch(user.actions.userResaleIntention.request());
        redirectToHome();
      }
    }

    if (isError) {
      const code = error?.code || error?.statusCode ? `${error?.code || error?.statusCode}:` : '';
      window.dataLayer.push({
        event: 'analytics-event',
        event_parameters: {
          event_name: 'callback_login_erro',
          cd_error_message: `${code}${slugify(error?.userMessage || error?.detail)}`,
          cd_method: usernameMethod,
        },
      });
    }
  }, [isSuccess, isError, isLoading, data]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Styles.FormLayout>
          <Styles.FormWrapper>
            <Text size="bodyMediumStandard">
              <Input
                id="username"
                autoComplete="username"
                required
                labelText={`CPF, e-mail ou cód. ${config.communication.userNamingConvention.singular}`}
                data-title="preencheu:login"
                data-eventname="interaction_login_form"
                disabled={isLoading}
                invalid={validationErrors.username?.message !== undefined}
                invalidText={validationErrors.username?.message}
                aria-label={`CPF, e-mail ou código ${config.communication.userNamingConvention.singular}`}
                rightIcon={
                  <Tooltip
                    align="start"
                    direction="bottom"
                    showArrow
                    size="standard"
                    aria-label="dúvidas de como acessar o portal"
                    text="Seu login é o mesmo utilizado no aplicativo. Caso não saiba seu código de Revendedor, faça login com seu CPF ou e-mail."
                  >
                    <Button
                      as="span"
                      variant="unstyled"
                      data-eventname="interaction_login"
                      data-title="tooltip"
                      onClick={handleOnTooltipTracking('click')}
                      onMouseEnter={handleOnTooltipTracking('mouseover')}
                    >
                      <QuestionMarkCircleIcon size={24} />
                    </Button>
                  </Tooltip>
                }
                {...register('username')}
                onBlur={handleTracking}
              />
            </Text>
          </Styles.FormWrapper>

          <Flex direction="column" gap="$2">
            <Text size="bodyMediumStandard">
              <Input
                autoComplete="password"
                id="password"
                labelText="Senha"
                aria-label="senha"
                required
                data-title="preencheu:senha"
                data-eventname="interaction_login_form"
                disabled={isLoading}
                rightIcon={
                  <Button
                    data-testid="toggle-password-visibility"
                    as="span"
                    variant="unstyled"
                    data-eventname="interaction_login"
                    data-title="click:mostrar-senha"
                    onClick={showPassword}
                    aria-label={
                      passwordType === 'password'
                        ? 'ativar visualização de caracteres'
                        : 'desativar visualização de caracteres'
                    }
                  >
                    {passwordType === 'password' ? <EyeIcon /> : <EyeOffIcon />}
                  </Button>
                }
                type={passwordType === 'password' ? 'password' : 'text'}
                {...register('password')}
                onBlur={handleTracking}
              />
            </Text>
          </Flex>

          <Styles.ForgotPassword direction="row" justify="right">
            <Link to="/recuperacao-de-senha">
              <ResponsiveFloraTypography
                data-flora="button"
                fontSize="bodySmallShort"
                textDecoration="underline"
                as="span"
                autotagging-flora="on"
                data-flora-text="esqueci-minha-senha"
                color="$actionableDefault"
              >
                Esqueci minha senha
              </ResponsiveFloraTypography>
            </Link>
          </Styles.ForgotPassword>
          <Flex direction="column" gap="$2">
            <FloraButton
              autotagging-flora="on"
              data-flora="button"
              data-flora-text="entrar"
              type="submit"
              isFull
              disabled={!isValid && !isSubmitting}
              isLoading={isLoading}
            >
              Entrar
            </FloraButton>
          </Flex>
        </Styles.FormLayout>
      </form>
      {isError && <MessageError message={error?.userMessage || error?.detail || ''} />}
    </>
  );
};
